<template>
  <div>
    <div
      class="w-screen h-screen fixed left-0 top-0 z-0"
      :style="`background-image:url(${require('@/assets/images/bg.jpg')});background-size:cover;background-repeat:no-repeat;background-position:center center`"
    />
    <div
      class="w-screen h-screen fixed left-0 top-0 z-1"
      :class="{
        'bg-overlay2': !!selectedEra
      }"
    />

    <BuildingGrid
      v-if="selectedEra"
      class="z-10"
      @save="onSave"
      @reuse="onReuse"
      @levelUp="onLevelUp"
      @clear="onClear"
    />

    <EraSelector
      v-if="!selectedEra"
      class="z-20"
      @select="changeEra"
    />

    <BuildingSelector
      v-else-if="selectedEra && editable"
      class="z-20"
      @newLayout="onNewLayout"
    />

    <SelectedEra
      v-if="selectedEra"
      class="z-20"
    />

    <LegalStuff />

    <CurrentLayoutUrl
      v-if="currentLayoutUrl"
      :current-layout-url="currentLayoutUrl"
      :modal-header-text="currentLayoutUrlModalText"
      @close="resetURLData"
    />

    <div
      v-if="isDebug"
      class="z-30 text-red-400 bottom-32 right-4 border border-red-400 rounded p-4 w-64 text-center absolute cursor-pointer"
      @click="showMaxUsedBuildingID"
    >
      Show Max Used Building ID
    </div>

    <WonderConfigurator
      v-if="configuringWonders"
    />

    <MuseumConfigurator
      v-if="configuringMuseums"
    />

    <Bonuses
      v-if="viewingBonuses"
    />
  </div>
</template>

<script>
import BuildingGrid from '@/components/BuildingGrid.vue';
import EraSelector from '@/components/EraSelector.vue';
import BuildingSelector from '@/components/BuildingSelector.vue';
import SelectedEra from '@/components/SelectedEra.vue';
import LayoutUrl from '@/roc/url';
import Grid from '@/roc/grid/Grid';
import CurrentLayoutUrl from '@/components/Modal/CurrentLayoutUrl.vue';
import LegalStuff from '@/components/Modal/LegalStuff.vue';
import WonderConfigurator from "@/components/Wonders/WonderConfigurator.vue";
import MuseumConfigurator from "@/components/Museum/MuseumConfigurator.vue";
import {computed, onBeforeMount, onBeforeUnmount, onMounted, ref} from 'vue';
import {useWonderStore} from '@/roc/stores/WonderStore';
import {useMuseumStore} from '@/roc/stores/MuseumStore';
import {useApplicationStore} from '@/roc/stores/ApplicationStore';
import {useExpansionStore} from '@/roc/stores/ExpansionStore';
import {useLayoutStore} from '@/roc/stores/LayoutStore';
import {updateLayoutForGridSizeChange} from '@/roc/url/DecodedLayout';
import Bonuses from '@/components/Bonuses/Bonuses.vue';

export default {
  components: {
    Bonuses,
    WonderConfigurator,
    LegalStuff,
    CurrentLayoutUrl,
    SelectedEra,
    BuildingSelector,
    EraSelector,
    BuildingGrid,
    MuseumConfigurator
  },
  setup () {
    const wonderStore = useWonderStore();
    const museumStore = useMuseumStore();
    const appStore = useApplicationStore();
    const expStore = useExpansionStore();
    const lytStore = useLayoutStore();

    const askToLeavePage = ref(true);
    const currentLayoutUrl = ref(null);
    const currentLayoutUrlModalText = ref("URL to this layout:");

    const isDebug = computed(() => {
      return process.env.NODE_ENV !== 'production';
    });
    
    const onLeavePage = (ev) => {
      if (askToLeavePage.value && appStore.selectedEra && appStore.editable && !isDebug.value) {
        ev.preventDefault();
        return ev.returnValue = 'Are you sure you want to leave this page? You will lose any unsaved changes.';
      }
    };

    const onSave = () => {
      currentLayoutUrl.value = LayoutUrl.encodeURL(
        appStore.selectedEra,
        Object.values(wonderStore.wonders),
        Object.values(museumStore.artifacts),
        Object.values(expStore.expansions),
        Object.values(lytStore.buildings),
      );
    };

    onBeforeMount(() => {
      window.addEventListener('beforeunload', onLeavePage, { capture: true });
    });
    
    onBeforeUnmount(() => {
      window.removeEventListener('beforeunload', onLeavePage, { capture: true });
    });

    onMounted(() => {
      if (screen.height <= 800) {
        Grid.CELL_SIZE = 20;
      }

      const _layout = LayoutUrl.decodeURL();
      if (_layout) {
        updateLayoutForGridSizeChange(_layout);

        lytStore.loadLayout(_layout.buildings);

        expStore.loadExpansionConfiguration(_layout.expansions);

        wonderStore.loadWonderConfiguration(_layout.wonders);

        museumStore.loadMuseumConfiguration(_layout.artifacts);

        appStore.changeEra(_layout.era);
        appStore.disableEditing();
      }
    });

    return {
      currentLayoutUrl,
      currentLayoutUrlModalText,

      isDebug,

      configuringWonders: computed(() => {
        return wonderStore.configuringWonders;
      }),

      configuringMuseums: computed(() => {
        return museumStore.configuringMuseums;
      }),

      viewingBonuses: computed(() => {
        return appStore.viewingBonuses;
      }),

      selectedEra: computed(() => {
        return appStore.selectedEra;
      }),

      editable: computed(() => {
        return appStore.editable;
      }),

      onSave,

      changeEra (eraId) {
        appStore.changeEra(eraId);
      },

      onReuse () {
        const url = new URL(window.location.href);
        window.history.pushState({}, '', url.origin);
        appStore.enableEditing();
      },

      onNewLayout () {
        const url = new URL(window.location.href);
        askToLeavePage.value = false;
        window.location.href = url.origin;
      },

      onLevelUp () {
        currentLayoutUrlModalText.value = "Level up done! Please re-save your layout to not lose the changes. New URL:"

        setTimeout(() => {
          onSave();
        }, 500);
      },

      showMaxUsedBuildingID () {
        const maxID = appStore.maxUsedBuildingId();

        alert(
          'MAX ID: ' + maxID + ', Next ID: ' + (maxID + 1)
        );
      },

      resetURLData () {
        currentLayoutUrl.value = null
        currentLayoutUrlModalText.value = "URL to this layout:"
      },

      onClear () {
        const url = new URL(window.location.href);
        window.history.pushState({}, '', url.origin);
      }
    }
  },
};
</script>
